import React, { SyntheticEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@asigna/kit';
import { acceptInviteMultisigRequest, getAllMyMultisigs, getPendingMultisigs } from 'api';
import cx from 'classnames';
import { NotificationCard, SafeAvatar } from 'components';
import { ShortText } from 'components/ShortText/ShortText';
import { useSidebarState } from 'hooks';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import LoadGif from 'images/loading-gif.gif';
import { toast } from 'sonner';
import { StxChain, useDeletePendingDialog, usePendingSafes, useSafes } from 'store';
import { IClaimMultiSignWallet } from 'types/api/ApiResponseTypes';
import { StxChainEnum } from 'types/enums';
import { shortenAddress } from 'utils/helper';

import { useTreasuryStyles } from '../useTreasuryStyles';

interface PendingSafeCardProps {
  pendingSafe: IClaimMultiSignWallet;
  addressWallet: string;
}

export const PendingSafeCard: React.FC<PendingSafeCardProps> = ({ pendingSafe, addressWallet }) => {
  const classes = useTreasuryStyles();
  const { setCurrentSafeId } = useSidebarState();
  const history = useHistory();
  const { setDeletePendingDialogOpen } = useDeletePendingDialog();
  const [network] = useNetwork();
  const net = StxChainEnum[network] as any as StxChain;
  const { setSafes } = useSafes();
  const { setPendingSafes } = usePendingSafes();
  const walletAccount = useAddress();

  const canJoin = pendingSafe.owners.find(
    (owner) => owner.address === addressWallet && !owner.verified
  );

  const handleJoin = (e: SyntheticEvent) => {
    e.stopPropagation();
    acceptInviteMultisigRequest(pendingSafe._id, net)
      .then((res) => {
        if (!res.data) return;
        Promise.all([getAllMyMultisigs(net), getPendingMultisigs(net)]).then(
          ([safes, pendingSafes]) => {
            setSafes(safes);
            setPendingSafes(pendingSafes);
          }
        );
        toast(
          () => (
            <NotificationCard
              title="Signer joined"
              message={`Signer ${shortenAddress(walletAccount || '')} joined ${
                pendingSafe.name
              } successfully.`}
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );
      })
      .catch((error) => console.error('An error occurred:', error));
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDeletePendingDialogOpen(pendingSafe._id);
  };

  return (
    <div
      className="flex flex-col items-center px-16 sm:px-24 py-16 sm:py-20 rounded-[12px] bg-black-2 border border-[.5px] border-black-4 w-full hover:border-primary hover:bg-primary-5 cursor-pointer"
      onClick={() => {
        setCurrentSafeId(pendingSafe._id);
        history.push(`/invite/${pendingSafe._id}`);
      }}
    >
      <div className="flex flex-col w-full">
        <div className={classes.inviteWaiting}>
          <div
            className={cx(
              classes.icon,
              'flex w-44 h-44 rounded-[50%] bg-white-10 items-center justify-center'
            )}
          >
            <SafeAvatar size="40px" address={pendingSafe._id} styleWrapper={classes.wrapperIcon} />
          </div>
          <div className={classes.inviteHeadWrapper}>
            <span className={classes.inviteSafeTitle}>
              <ShortText text={pendingSafe.name} limit={18} />
            </span>
            <div className={classes.inviteHead}>
              <img width={12} height={12} src={LoadGif} alt="loading" />
              <p className="text-r12 text-regular">Waiting owners to join</p>
            </div>
          </div>
        </div>
        <div className="flex gap-16 w-full justify-around my-8">
          <div className="flex items-center gap-4 text-r12">
            <p className="text-regular">Joined:</p>
            <p>{pendingSafe.owners.filter((owner) => owner.verified).length}</p>
          </div>
          <div className="flex items-center gap-4 text-r12">
            <p className="text-regular">Pending:</p>
            <p>{pendingSafe.owners.filter((owner) => !owner.verified).length}</p>
          </div>
        </div>
      </div>
      <div className="w-full h-[.5px] bg-black-4 mx-[-24px]" />
      <div className="flex items-center mt-16 gap-12 w-full">
        {canJoin ? (
          <>
            <Button
              onClick={handleDelete}
              message="Delete"
              colorType="secondary"
              size="md"
              fullWidth
            />
            <Button onClick={handleJoin} message="Join" colorType="primary" size="md" fullWidth />
          </>
        ) : (
          <Button
            onClick={handleDelete}
            message="Delete"
            colorType="secondary"
            size="md"
            fullWidth
          />
        )}
      </div>
    </div>
  );
};
